/**
 * Implementation scheduling math (ADO #1661).
 *
 * Pure: no dataverse or entity imports, so it unit-tests without a live
 * client. `stampProject.ts` owns the reads and writes and calls in here for
 * every date it persists.
 *
 * Two ways a task gets a target date:
 *
 *   1. **T-minus pin.** The template says "this targets 45 days before the
 *      effective date" and the task lands there, full stop. This is how the
 *      implementation team actually plans: they count backward from go-live,
 *      and dependency chains are the exception rather than the rule.
 *   2. **CPM.** No offset, so the task's date derives from its duration and
 *      its predecessors, exactly as it did before pins existed.
 *
 * With no offsets anywhere, this produces the same schedule the pre-#1661
 * engine did, one bug excepted (see "Business days and time zones").
 *
 * ## Pins win over predecessors
 *
 * If a predecessor's finish would push a pinned task past its pin, the pin
 * holds and the clash is reported as a conflict instead of being silently
 * resolved. An imp manager's mental model is the calendar; a date that quietly
 * slides because of a dependency she did not author is what makes people stop
 * trusting auto-dates. Showing the conflict lets her fix the real problem.
 *
 * ## Business days and time zones
 *
 * Offsets are CALENDAR days, because "T-minus 45" is a calendar statement.
 * Durations and lags stay BUSINESS days, which is what they always meant. A
 * pin that lands on a weekend rolls BACK to the Friday, never forward: a
 * T-minus date is a deadline, and rolling forward silently spends a day of
 * float.
 *
 * All of it runs on UTC accessors. The engine this replaced parsed
 * 'YYYY-MM-DD' as UTC midnight and then read the day of week with the LOCAL
 * getDay(), which in any negative-offset zone reports the PREVIOUS day. In
 * US Eastern that shifted every weekend test by one, so weekend skipping
 * skipped the wrong days and targets landed on Saturdays: Thursday 2026-09-17
 * plus two business days returned Saturday 2026-09-19 instead of Monday
 * 2026-09-21. Existing stored dates keep whatever they were given until the
 * project is rescheduled.
 */

const DAY_MS = 86_400_000;

/** 'YYYY-MM-DD' (or a full ISO string) to a UTC-midnight Date. */
function parseDay(dateStr: string): Date {
  return new Date(`${dateStr.split('T')[0]}T00:00:00Z`);
}

function toDay(d: Date): string {
  return d.toISOString().split('T')[0]!;
}

/** Saturday or Sunday, read in UTC to match how the day was parsed. */
function isWeekend(d: Date): boolean {
  const dow = d.getUTCDay();
  return dow === 0 || dow === 6;
}

/**
 * Add N business days, skipping Saturdays and Sundays. Negative N walks
 * backward. Durations and lags are expressed this way, so "3 days" means
 * three work days.
 */
export function addBusinessDays(dateStr: string, days: number): string {
  const d = parseDay(dateStr);
  if (days === 0) return toDay(d);
  let remaining = Math.abs(days);
  const step = days > 0 ? DAY_MS : -DAY_MS;
  let cursor = d.getTime();
  while (remaining > 0) {
    cursor += step;
    if (!isWeekend(new Date(cursor))) remaining--;
  }
  return toDay(new Date(cursor));
}

/** Business days from start to end, signed (negative when end precedes start). */
export function businessDaysBetween(startStr: string, endStr: string): number {
  const start = parseDay(startStr);
  const end = parseDay(endStr);
  if (start.getTime() === end.getTime()) return 0;
  const reverse = start > end;
  let cursor = (reverse ? end : start).getTime();
  const stop = (reverse ? start : end).getTime();
  let count = 0;
  while (cursor < stop) {
    cursor += DAY_MS;
    if (!isWeekend(new Date(cursor))) count++;
  }
  return reverse ? -count : count;
}

/** Subtract N calendar days. T-minus offsets are calendar, not business, days. */
export function subtractCalendarDays(dateStr: string, days: number): string {
  return toDay(new Date(parseDay(dateStr).getTime() - days * DAY_MS));
}

/**
 * Move a weekend date back to the preceding Friday. Deadlines roll backward:
 * rolling a T-minus date forward would hand back float the template author
 * deliberately reserved.
 */
export function rollBackToBusinessDay(dateStr: string): string {
  let cursor = parseDay(dateStr).getTime();
  while (isWeekend(new Date(cursor))) cursor -= DAY_MS;
  return toDay(new Date(cursor));
}

// =============================================================================
// Types
// =============================================================================

export const DEP_FS = 100000000;
export const DEP_SS = 100000001;
export const DEP_FF = 100000002;
export const DEP_SF = 100000003;

export interface ScheduleTask {
  id: string;
  /** Business days of work. */
  duration: number;
  /**
   * T-minus: calendar days before the anchor date this task targets.
   * Undefined (or null) leaves the task on CPM, as before #1661.
   */
  offsetDays?: number | null;
}

export interface ScheduleDep {
  predecessorId: string;
  successorId: string;
  lagDays: number;
  dependencyType: number;
}

export interface TaskDates {
  targetStart: string;
  targetEnd: string;
}

/** A pinned task whose predecessors cannot finish in time for its pin. */
export interface ScheduleConflict {
  taskId: string;
  /** Where the pin holds the task. */
  pinnedStart: string;
  /** The earliest start the dependency graph actually allows. */
  earliestStart: string;
  /** Business days the predecessors overrun the pin by. */
  overrunBusinessDays: number;
}

export interface Schedule {
  projectStart: string;
  dates: Map<string, TaskDates>;
  conflicts: ScheduleConflict[];
}

// =============================================================================
// Pins
// =============================================================================

/**
 * Resolve every T-minus offset against the anchor. A pinned task's END is the
 * promise ("in my hands 45 days before go-live"), so the offset sets targetEnd
 * and the duration backs targetStart out from it.
 */
export function resolvePins(
  tasks: ScheduleTask[],
  anchorDate: string,
): Map<string, TaskDates> {
  const pins = new Map<string, TaskDates>();
  for (const t of tasks) {
    if (t.offsetDays == null) continue;
    const targetEnd = rollBackToBusinessDay(subtractCalendarDays(anchorDate, t.offsetDays));
    pins.set(t.id, { targetEnd, targetStart: addBusinessDays(targetEnd, -t.duration) });
  }
  return pins;
}

// =============================================================================
// Forward pass
// =============================================================================

/**
 * Forward-pass CPM from a project start date, honoring pins.
 *
 * Tasks are walked in topological order. Each dependency type constrains the
 * successor differently:
 *   - FS: succStart >= predEnd + lag
 *   - SS: succStart >= predStart + lag
 *   - FF: succEnd >= predEnd + lag, so succStart >= predEnd + lag - duration
 *   - SF: succEnd >= predStart + lag, so succStart >= predStart + lag - duration
 *
 * An unpinned successor takes the max across all inbound constraints, floored
 * at the project start. A pinned successor keeps its pin and records a conflict
 * when the constraints demand a later start than the pin allows.
 */
export function calculateDates(
  tasks: ScheduleTask[],
  deps: ScheduleDep[],
  projectStart: string,
  pins: Map<string, TaskDates> = new Map(),
): { dates: Map<string, TaskDates>; conflicts: ScheduleConflict[] } {
  const dates = new Map<string, TaskDates>();
  // Latest start any predecessor chain implies for a pinned task, so a
  // conflict reports the real overrun rather than the first one seen.
  const impliedStart = new Map<string, string>();

  const successors = new Map<string, Array<{ successorId: string; lagDays: number; dependencyType: number }>>();
  const inDegree = new Map<string, number>();
  for (const task of tasks) {
    inDegree.set(task.id, 0);
    successors.set(task.id, []);
    const pin = pins.get(task.id);
    if (pin) dates.set(task.id, pin);
  }

  for (const dep of deps) {
    if (!inDegree.has(dep.predecessorId) || !inDegree.has(dep.successorId)) continue;
    successors.get(dep.predecessorId)!.push({
      successorId: dep.successorId,
      lagDays: dep.lagDays,
      dependencyType: dep.dependencyType,
    });
    inDegree.set(dep.successorId, (inDegree.get(dep.successorId) ?? 0) + 1);
  }

  const queue: string[] = [];
  for (const [id, degree] of inDegree) {
    if (degree === 0) queue.push(id);
  }

  const taskMap = new Map(tasks.map((t) => [t.id, t]));

  while (queue.length > 0) {
    const taskId = queue.shift()!;
    const task = taskMap.get(taskId)!;

    // Unpinned and unreached by any predecessor: start at the project start.
    if (!dates.has(taskId)) {
      dates.set(taskId, {
        targetStart: projectStart,
        targetEnd: addBusinessDays(projectStart, task.duration),
      });
    }

    const predStart = dates.get(taskId)!.targetStart;
    const predEnd = dates.get(taskId)!.targetEnd;

    for (const { successorId, lagDays, dependencyType } of successors.get(taskId) ?? []) {
      const succTask = taskMap.get(successorId)!;
      const succDuration = succTask.duration;

      let candidateStart: string;
      switch (dependencyType) {
        case DEP_SS:
          candidateStart = addBusinessDays(predStart, lagDays);
          break;
        case DEP_FF:
          candidateStart = addBusinessDays(predEnd, lagDays - succDuration);
          break;
        case DEP_SF:
          candidateStart = addBusinessDays(predStart, lagDays - succDuration);
          break;
        case DEP_FS:
        default:
          candidateStart = addBusinessDays(predEnd, lagDays);
          break;
      }

      if (pins.has(successorId)) {
        // The pin holds. Remember the worst demand for the conflict report.
        const seen = impliedStart.get(successorId);
        if (!seen || candidateStart > seen) impliedStart.set(successorId, candidateStart);
      } else {
        // Floor at project start so negative lag or SF never pushes a
        // successor earlier than the project itself.
        if (candidateStart < projectStart) candidateStart = projectStart;
        const existing = dates.get(successorId);
        if (!existing || candidateStart > existing.targetStart) {
          dates.set(successorId, {
            targetStart: candidateStart,
            targetEnd: addBusinessDays(candidateStart, succDuration),
          });
        }
      }

      inDegree.set(successorId, (inDegree.get(successorId) ?? 0) - 1);
      if (inDegree.get(successorId) === 0) queue.push(successorId);
    }
  }

  // Cycles and isolated tasks the walk never reached.
  for (const task of tasks) {
    if (!dates.has(task.id)) {
      dates.set(task.id, {
        targetStart: projectStart,
        targetEnd: addBusinessDays(projectStart, task.duration),
      });
    }
  }

  const conflicts: ScheduleConflict[] = [];
  for (const [taskId, implied] of impliedStart) {
    const pin = pins.get(taskId)!;
    if (implied > pin.targetStart) {
      conflicts.push({
        taskId,
        pinnedStart: pin.targetStart,
        earliestStart: implied,
        overrunBusinessDays: businessDaysBetween(pin.targetStart, implied),
      });
    }
  }
  conflicts.sort((a, b) => b.overrunBusinessDays - a.overrunBusinessDays || a.taskId.localeCompare(b.taskId));

  return { dates, conflicts };
}

/**
 * Count back from the anchor (effective date, or term date for a termination).
 *
 * A probe forward pass finds the critical-path length in business days, which
 * sets the project start so the critical chain lands exactly on the anchor.
 * Pins are then applied, and the project start moves earlier if any pin starts
 * before it, so an early T-minus task is never floored away.
 *
 * If the critical path is longer than the time until the anchor, the project
 * start lands in the past. That is intentional: tasks render overdue from day
 * one and the coordinator sees immediately that they are behind.
 */
export function scheduleFromEffective(
  tasks: ScheduleTask[],
  deps: ScheduleDep[],
  anchorDate: string,
): Schedule {
  // Probe from a known Monday so the business-day count is clean. Pins are
  // deliberately excluded here: the probe measures the dependency graph, and
  // including them would let a pin change the project start twice.
  const probeStart = '2000-01-03';
  const probe = calculateDates(tasks, deps, probeStart);
  let maxEnd = probeStart;
  for (const v of probe.dates.values()) {
    if (v.targetEnd > maxEnd) maxEnd = v.targetEnd;
  }
  const totalBusinessDays = businessDaysBetween(probeStart, maxEnd);

  const pins = resolvePins(tasks, anchorDate);
  let projectStart = addBusinessDays(anchorDate, -totalBusinessDays);
  for (const pin of pins.values()) {
    if (pin.targetStart < projectStart) projectStart = pin.targetStart;
  }

  const { dates, conflicts } = calculateDates(tasks, deps, projectStart, pins);
  return { projectStart, dates, conflicts };
}
